
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../../firebase';
import { ref, get, set, remove } from 'firebase/database';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { Mail, Lock, ShieldCheck, ArrowRight, AlertCircle, RefreshCw } from 'lucide-react';

const AdminLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // 1. Firebase Auth ile giriş yap
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      console.log("Giriş Başarılı. Gerçek Auth UID:", user.uid);

      // 2. Veritabanında bu UID ile kayıt var mı bak
      try {
        const userRef = ref(db, `users/${user.uid}`);
        const snapshot = await get(userRef);
        
        if (snapshot.exists() && snapshot.val().isAdmin === true) {
          navigate('/admin/dashboard');
          return;
        }
      } catch (e) {
        console.warn("Kullanıcı profili okunurken izin hatası alındı. Onarım deneniyor...");
      }

      // 3. Eğer UID ile bulunamadıysa, e-posta ile tüm kullanıcıları tara (Onarım Modu)
      setRepairing(true);
      
      try {
        const allUsersRef = ref(db, 'users');
        const allUsersSnap = await get(allUsersRef);
        
        if (allUsersSnap.exists()) {
          const allUsers = allUsersSnap.val();
          const wrongKey = Object.keys(allUsers).find(key => 
            allUsers[key].email?.toLowerCase() === email.toLowerCase() && 
            allUsers[key].isAdmin === true &&
            key !== user.uid
          );

          if (wrongKey) {
            console.log("Onarım: Veriler doğru anahtara taşınıyor...");
            const oldData = allUsers[wrongKey];
            await set(ref(db, `users/${user.uid}`), {
              ...oldData,
              uid: user.uid
            });
            
            setRepairing(false);
            navigate('/admin/dashboard');
            return;
          }
        }
      } catch (repairError: any) {
        console.error("Onarım Modu Başarısız (İzin Yok):", repairError.message);
      }

      setRepairing(false);
      await signOut(auth);
      setError('Bu hesaba ait bir yönetici profili bulunamadı veya yetkiniz eksik.');

    } catch (err: any) {
      console.error("Giriş hatası:", err);
      setRepairing(false);
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        setError('E-posta adresi veya şifre hatalı.');
      } else {
        setError('Giriş yapılırken teknik bir hata oluştu: ' + (err.message || ''));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-[3rem] p-10 md:p-14 shadow-2xl border border-stone-100 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-2 bg-stone-900"></div>
          
          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-amber-50 rounded-3xl flex items-center justify-center text-amber-800 mx-auto mb-6 shadow-inner">
              <ShieldCheck size={40} />
            </div>
            <h1 className="text-2xl font-serif font-bold text-stone-900">Yönetim Paneli</h1>
            <p className="text-stone-400 mt-2 text-sm italic font-medium">Lumina Home • Güvenli Giriş</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">E-posta</label>
              <div className="relative">
                <Mail className="absolute left-5 top-1/2 -translate-y-1/2 text-stone-300" size={18} />
                <input 
                  type="email" required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 pl-14 pr-6 focus:ring-2 focus:ring-amber-500 focus:bg-white transition-all outline-none font-medium text-stone-900"
                  placeholder="admin@luminahome.com"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Şifre</label>
              <div className="relative">
                <Lock className="absolute left-5 top-1/2 -translate-y-1/2 text-stone-300" size={18} />
                <input 
                  type="password" required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 pl-14 pr-6 focus:ring-2 focus:ring-amber-500 focus:bg-white transition-all outline-none font-medium text-stone-900"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 p-4 rounded-2xl flex items-start space-x-3 border border-red-100">
                <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                <span className="text-xs font-bold">{error}</span>
              </div>
            )}

            <button 
              type="submit"
              disabled={loading}
              className="w-full bg-stone-900 text-white py-5 rounded-2xl font-bold hover:bg-amber-800 active:scale-95 transition-all shadow-xl shadow-stone-900/10 disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {repairing ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  <span>Doğrulanıyor...</span>
                </>
              ) : (
                <>
                  <span>{loading ? 'Bağlanıyor...' : 'Giriş Yap'}</span>
                  {!loading && <ArrowRight size={18} />}
                </>
              )}
            </button>
          </form>

          <div className="mt-12 pt-8 border-t border-stone-50 text-center">
            <p className="text-[10px] font-black text-stone-300 uppercase tracking-[0.3em]">
              Lumina Home Boutique System
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
